import {useHistoryAssetsQuery} from '@domain/assets'
import {type AmpelPriceLevels} from '@domain/electricity'
import {useScheduleQuery} from '@domain/schedule'
import {type ScheduleItem as ScheduleItemType} from '@domain/schedule'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {Box} from '@mui/material'
import {LinearScaleFn, TimeScaleFn} from '@widgets/common-d3-chart'
import {Moment} from 'moment-timezone'
import React, {useState, useMemo} from 'react'

import {
  useAssetHistoryDataByScheduleItemId,
  useHistoryStoragesByIdQuery,
  useScheduleCommentsByScheduleItemId
} from '../../hooks'
import type {DatetimeValue} from '../../types'
import {usePlanningChartScheduleData, usePlanningChartStartEnd} from '../providers'

import {ScheduleItemPopper} from './popper'
import {ScheduleItemPopperState} from './popper/ScheduleItemPopperContent'
import {ScheduleItem} from './ScheduleItem'

const noop = () => undefined

interface ScheduleItemsProps {
  startOfPlan: Moment
  endOfPlan: Moment
  priceLevels: AmpelPriceLevels
  prices: DatetimeValue<Moment>[]
  scheduleRowHeight: number
  scheduleRowPadding: number
  xScale: TimeScaleFn
  yScale: LinearScaleFn
  shouldShowFixedForOptimizerInfo: boolean
  shouldShowColorCodedElectricityPrices: boolean
  shouldShowExactElectricityPrices: boolean
  shouldShowStartEndTime: boolean
  shouldShowScheduleItemComments: boolean
  editDialog?: React.ReactNode
  updateScheduleItem?: (item: ScheduleItemType) => void
  isScheduleSubmitting?: boolean
}

const _ScheduleItemsContainer: React.FC<ScheduleItemsProps> = ({
  startOfPlan,
  endOfPlan,
  priceLevels,
  prices,
  scheduleRowHeight,
  scheduleRowPadding,
  xScale,
  yScale,
  shouldShowFixedForOptimizerInfo,
  shouldShowColorCodedElectricityPrices,
  shouldShowExactElectricityPrices,
  shouldShowStartEndTime,
  shouldShowScheduleItemComments,
  editDialog,
  updateScheduleItem,
  isScheduleSubmitting = false
}) => {
  const timezoneId = useUpmPlantTimezone()
  const {scheduleId} = usePlanningChartScheduleData()
  const {plantId} = useUpmPlantContext()
  const {data: schedule, isFetching: isScheduleFetching} = useScheduleQuery({
    plantId,
    range: [startOfPlan, endOfPlan],
    scheduleId
  })
  const assetDataByScheduleItemId = useAssetHistoryDataByScheduleItemId({
    timeFrame: [startOfPlan, endOfPlan],
    schedule
  })
  const isReadOnly = !editDialog

  const {startOfChart, endOfChart} = usePlanningChartStartEnd()

  const scheduleItems = Object.values(schedule?.schedules ?? {})

  const {data: sortedAssets} = useHistoryAssetsQuery({
    plantId,
    timeFrame: [startOfPlan, endOfPlan],
    sorted: true
  })

  const {data: storagesById} = useHistoryStoragesByIdQuery({
    timeFrame: [startOfPlan, endOfPlan],
    throwOnError: false
  })

  const isScheduleItemReadOnly = isReadOnly || isScheduleFetching || isScheduleSubmitting

  const [scheduleItemPopperState, setScheduleItemPopperState] = useState<ScheduleItemPopperState>()

  const scheduleItemsIds = (shouldShowScheduleItemComments ? scheduleItems : []).map(({id}) => id)

  const {data: commentsByScheduleItemId} = useScheduleCommentsByScheduleItemId(
    scheduleItemsIds,
    !shouldShowScheduleItemComments
  )

  const sortedAssetsIds = useMemo(() => sortedAssets?.map(({id}) => id), [sortedAssets])

  const canShowPopper = assetDataByScheduleItemId && scheduleItemPopperState && storagesById
  const canShowScheduleItems = sortedAssetsIds && assetDataByScheduleItemId

  // filter only visible range ( DOM virtualization of schedule items)
  const visibleScheduleItems = useMemo(() => {
    return scheduleItems.filter(
      (item) => endOfChart.isSameOrAfter(item.start) && startOfChart.isSameOrBefore(item.end)
    )
  }, [scheduleItems, startOfChart, endOfChart])
  return (
    <>
      <Box sx={{position: 'absolute', zIndex: 1}}>
        {canShowScheduleItems
          ? visibleScheduleItems.map((item) => {
              const foundComment = commentsByScheduleItemId?.[item.id]
              const rowIndex = sortedAssetsIds.findIndex((assetId) => assetId === item.assetId)
              const assetData = assetDataByScheduleItemId[item.id]
              const assetType = assetData.asset.type
              return (
                <ScheduleItem
                  key={`schedule_item_${item.id}`}
                  timezoneId={timezoneId}
                  startOfPlan={startOfPlan}
                  endOfPlan={endOfPlan}
                  item={item}
                  scheduleRowHeight={scheduleRowHeight}
                  scheduleRowPadding={scheduleRowPadding}
                  xScale={xScale}
                  yScale={yScale}
                  priceLevels={priceLevels}
                  prices={prices}
                  assetType={assetType}
                  operationMode={assetData.operationMode}
                  comment={foundComment}
                  setScheduleItemPopperOpen={setScheduleItemPopperState}
                  updateScheduleItem={updateScheduleItem ?? noop}
                  isReadOnly={isScheduleItemReadOnly}
                  rowIndex={rowIndex}
                  shouldShowStartEndTimeLabels={shouldShowStartEndTime}
                  enableColorCodedElectricityPrices={shouldShowColorCodedElectricityPrices}
                  enableExactElectricityPrices={shouldShowExactElectricityPrices}
                />
              )
            })
          : undefined}
      </Box>
      {canShowPopper && (
        <ScheduleItemPopper
          scheduleItemData={scheduleItemPopperState}
          assetHistoryDataByItemId={assetDataByScheduleItemId}
          shouldShowFixedForOptimizerInfo={shouldShowFixedForOptimizerInfo}
          shouldShowColorCodedElectricityPrices={shouldShowColorCodedElectricityPrices}
          shouldShowExactElectricityPrices={shouldShowExactElectricityPrices}
        />
      )}
      {editDialog}
    </>
  )
}
export const ScheduleItemsContainer = React.memo(_ScheduleItemsContainer)
ScheduleItemsContainer.displayName = 'ScheduleItemsContainer'
